import React from 'react';
import { Link } from 'react-router-dom';
import { 
  QrCode, 
  Shield, 
  Globe, 
  Smartphone, 
  Heart,
  CheckCircle,
  Star,
  ArrowRight,
  Users,
  Zap,
  WifiOff,
  Lock,
  Server,
  Eye,
  Camera,
  Download,
  Wallet
} from 'lucide-react';

const ProductLandingPage = () => {
  const features = [
    {
      icon: <QrCode className="w-7 h-7" />,
      title: "Instant QR Code Access",
      description: "Generate a unique QR code with your vital medical information. Scannable by any smartphone camera."
    },
    {
      icon: <WifiOff className="w-7 h-7" />,
      title: "Works 100% Offline",
      description: "All data encoded directly in the QR code. No internet connection needed - works anywhere."
    },
    {
      icon: <Globe className="w-7 h-7" />,
      title: "20+ Languages",
      description: "Auto-detects viewer's language. Critical for international travelers and emergencies abroad."
    },
    {
      icon: <Shield className="w-7 h-7" />,
      title: "Privacy First",
      description: "You control what information to include. No account required. Your data stays with you."
    },
    {
      icon: <Smartphone className="w-7 h-7" />,
      title: "No App Needed",
      description: "Anyone can scan your QR code with their phone camera. No special app required to view."
    },
    {
      icon: <Zap className="w-7 h-7" />,
      title: "Instant Setup",
      description: "Create your emergency card in under 2 minutes. Print, save, or add to your phone wallet."
    }
  ];

  const howItWorks = [
    {
      step: "1",
      icon: <Eye className="w-6 h-6" />,
      title: "Enter Your Info",
      description: "Fill in your medical details, allergies, medications, and emergency contacts."
    },
    {
      step: "2",
      icon: <QrCode className="w-6 h-6" />,
      title: "Generate QR Code",
      description: "Your information is encoded into a unique, scannable QR code instantly."
    },
    {
      step: "3",
      icon: <Download className="w-6 h-6" />,
      title: "Save & Print",
      description: "Download your card, print it, or save to your phone's lock screen."
    },
    {
      step: "4",
      icon: <Camera className="w-6 h-6" />,
      title: "Scan in Emergency",
      description: "First responders scan your QR to see your medical info instantly."
    }
  ];

  const whatYouCanStore = [
    "Name, Age & Photo",
    "Blood Type",
    "Medical Conditions",
    "Allergies (Drug, Food, Environmental)",
    "Current Medications & Dosages",
    "Emergency Contacts",
    "Primary Doctor Information",
    "Special Instructions (DNR, Religious)",
    "Insurance Information"
  ];

  const whoIsItFor = [
    { icon: <Users className="w-5 h-5" />, title: "Seniors & Elderly", desc: "Manage multiple medications and conditions" },
    { icon: <Globe className="w-5 h-5" />, title: "Travelers", desc: "Stay safe abroad with multilingual cards" },
    { icon: <Heart className="w-5 h-5" />, title: "Parents & Families", desc: "Cards for kids, babysitter access" },
    { icon: <Shield className="w-5 h-5" />, title: "Chronic Conditions", desc: "Diabetes, epilepsy, heart conditions" }
  ];

  const testimonials = [
    {
      quote: "As a diabetic who travels frequently, this gives me peace of mind. First responders in Japan could read my info in Japanese!",
      author: "Sarah M.",
      role: "Business Traveler",
      rating: 5
    },
    {
      quote: "I made cards for my entire family after my father's stroke. Now all his medications are one scan away.",
      author: "Michael R.",
      role: "Family Caregiver",
      rating: 5
    },
    {
      quote: "Simple, affordable, and it works. I tested it with my local EMT friends and they loved it.",
      author: "David K.",
      role: "Retired Firefighter",
      rating: 5
    }
  ];

  const securityFeatures = [
    { icon: <Lock className="w-4 h-4" />, text: "No account required" },
    { icon: <Server className="w-4 h-4" />, text: "Data never stored on servers" },
    { icon: <Shield className="w-4 h-4" />, text: "You control your information" },
    { icon: <Eye className="w-4 h-4" />, text: "Works completely offline" }
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}>
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-gradient-to-br from-#D92B2B to-#C02424 rounded-xl flex items-center justify-center shadow-lg shadow-[#D92B2B]/20">
              <QrCode className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="font-bold text-lg text-gray-900">My EmergencyCard</span>
              <span className="hidden sm:inline text-xs text-gray-400 ml-2">by Hikari Grove</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/how-it-works" className="hidden sm:block text-gray-600 hover:text-[#C02424] text-sm font-medium transition-colors">
              How It Works
            </Link>
            <Link 
              to="/create" 
              className="bg-[#D92B2B] hover:bg-[#C02424] text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-all shadow-lg shadow-[#D92B2B]/25 hover:shadow-[#D92B2B]/40"
            >
              Create Card — $1
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#FEF2F2]/80 to-white"></div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Content */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-[#FEE2E2] text-[#A91F1F] px-3 py-1.5 rounded-full text-xs font-semibold mb-6">
                <Star className="w-3.5 h-3.5 fill-[#D92B2B]" />
                Trusted by 50,000+ users worldwide
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-5 leading-[1.1] tracking-tight">
                Save a Life.<br />
                <span className="text-[#D92B2B]">Yours.</span>
              </h1>
              
              <p className="text-lg text-gray-600 mb-8 leading-relaxed max-w-xl mx-auto lg:mx-0">
                Create a digital emergency medical card with QR code. Store vital health info, allergies, medications & emergency contacts. 
                <strong className="text-gray-800"> Works offline. No app needed.</strong>
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start items-center mb-8">
                <Link 
                  to="/create"
                  className="w-full sm:w-auto bg-[#D92B2B] hover:bg-[#C02424] text-white px-8 py-4 rounded-full font-semibold text-base transition-all shadow-xl shadow-[#D92B2B]/30 hover:shadow-[#D92B2B]/40 flex items-center justify-center gap-2"
                >
                  Create Your Card — $1 <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

              <p className="text-sm text-gray-500">One-time payment • No subscription • Lifetime access</p>
            </div>

            {/* Right: Visual */}
            <div className="relative">
              <div className="bg-white rounded-3xl shadow-2xl p-6 sm:p-8 border border-gray-100 max-w-sm mx-auto">
                {/* Mock Phone Frame */}
                <div className="bg-gray-900 rounded-2xl p-4 mb-4">
                  <div className="flex items-center justify-center gap-1 mb-3">
                    <div className="w-12 h-1 bg-gray-700 rounded-full"></div>
                  </div>
                  <div className="bg-white rounded-xl p-4">
                    <div className="w-32 h-32 mx-auto bg-gradient-to-br from-gray-100 to-gray-50 rounded-xl flex items-center justify-center mb-3 border-2 border-dashed border-gray-200">
                      <QrCode className="w-20 h-20 text-gray-400" />
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-gray-500 mb-1">Scan for medical info</p>
                      <p className="text-sm font-semibold text-gray-900">John Smith</p>
                    </div>
                  </div>
                </div>
                
                {/* Sample Data Preview */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                    <span className="text-gray-600">Blood Type:</span>
                    <span className="font-medium text-gray-900">A+</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                    <span className="text-gray-600">Allergies:</span>
                    <span className="font-medium text-gray-900">Penicillin</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    <span className="text-gray-600">Emergency:</span>
                    <span className="font-medium text-gray-900">Jane (Wife)</span>
                  </div>
                </div>
              </div>
              
              {/* Floating badges */}
              <div className="absolute -top-4 -right-4 bg-green-500 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg">
                Works Offline
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust & Security Bar */}
      <section className="bg-gray-50 border-y border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
          <div className="flex flex-wrap justify-center gap-6 sm:gap-10">
            {securityFeatures.map((item, index) => (
              <div key={index} className="flex items-center gap-2 text-sm text-gray-600">
                <span className="text-[#D92B2B]">{item.icon}</span>
                {item.text}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Create your emergency medical card in under 2 minutes
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {howItWorks.map((item, index) => (
              <div key={index} className="relative">
                <div className="bg-[#FEF2F2] rounded-2xl p-6 h-full border border-#FEE2E2 hover:border-rose-200 transition-colors">
                  <div className="w-10 h-10 bg-[#D92B2B] text-white rounded-xl flex items-center justify-center font-bold text-lg mb-4">
                    {item.step}
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-600">{item.description}</p>
                </div>
                {index < howItWorks.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                    <ArrowRight className="w-5 h-5 text-rose-300" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Everything You Need in an Emergency
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              When you can't speak for yourself, your emergency card speaks for you.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-6 border border-gray-100 hover:border-rose-200 hover:shadow-lg transition-all"
              >
                <div className="w-12 h-12 bg-[#FEE2E2] rounded-xl flex items-center justify-center text-[#C02424] mb-4">
                  {feature.icon}
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What You Can Store */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                What Information Can You Store?
              </h2>
              <p className="text-gray-600 mb-8 leading-relaxed">
                Your emergency card contains all the critical information first responders need to help you in a medical emergency.
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                {whatYouCanStore.map((item, index) => (
                  <div key={index} className="flex items-center gap-3 bg-gray-50 rounded-lg px-4 py-3">
                    <CheckCircle className="w-5 h-5 text-[#D92B2B] flex-shrink-0" />
                    <span className="text-sm text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-gradient-to-br from-[#FEF2F2] to-amber-50 rounded-3xl p-6 sm:p-8">
              <div className="space-y-4">
                <div className="bg-white rounded-xl p-4 shadow-sm">
                  <div className="text-xs font-semibold text-[#C02424] uppercase tracking-wide mb-1">Medical Conditions</div>
                  <div className="text-gray-800">Type 2 Diabetes, Hypertension</div>
                </div>
                <div className="bg-white rounded-xl p-4 shadow-sm">
                  <div className="text-xs font-semibold text-amber-600 uppercase tracking-wide mb-1">Allergies</div>
                  <div className="text-gray-800">Penicillin, Shellfish, Latex</div>
                </div>
                <div className="bg-white rounded-xl p-4 shadow-sm">
                  <div className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-1">Medications</div>
                  <div className="text-gray-800">Metformin 500mg, Lisinopril 10mg</div>
                </div>
                <div className="bg-white rounded-xl p-4 shadow-sm">
                  <div className="text-xs font-semibold text-green-600 uppercase tracking-wide mb-1">Emergency Contact</div>
                  <div className="text-gray-800">Jane Doe (Wife) - (555) 123-4567</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who Is It For */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Who Is This For?
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whoIsItFor.map((item, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 text-center border border-gray-100 hover:border-rose-200 transition-colors">
                <div className="w-12 h-12 bg-[#FEE2E2] rounded-full flex items-center justify-center text-[#C02424] mx-auto mb-4">
                  {item.icon}
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              What Our Users Say
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4 leading-relaxed">"{testimonial.quote}"</p>
                <div>
                  <div className="font-semibold text-gray-900">{testimonial.author}</div>
                  <div className="text-sm text-gray-500">{testimonial.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing CTA */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-#D92B2B to-#C02424">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            One Dollar. One QR Code. One Life Saved.
          </h2>
          <p className="text-#FEE2E2 text-lg mb-8 max-w-2xl mx-auto">
            For the cost of a coffee, protect yourself and your loved ones. 
            Create your emergency medical card today.
          </p>
          <div className="bg-white rounded-2xl p-8 max-w-md mx-auto shadow-2xl">
            <div className="text-5xl font-bold text-gray-900 mb-2">$1</div>
            <div className="text-gray-500 mb-6">One-time payment • Lifetime access</div>
            <ul className="text-left space-y-3 mb-8">
              <li className="flex items-center gap-2 text-gray-700">
                <CheckCircle className="w-5 h-5 text-green-500" /> Unlimited card updates
              </li>
              <li className="flex items-center gap-2 text-gray-700">
                <CheckCircle className="w-5 h-5 text-green-500" /> Print, save, or add to wallet
              </li>
              <li className="flex items-center gap-2 text-gray-700">
                <CheckCircle className="w-5 h-5 text-green-500" /> 20+ language support
              </li>
              <li className="flex items-center gap-2 text-gray-700">
                <CheckCircle className="w-5 h-5 text-green-500" /> Works offline forever
              </li>
            </ul>
            <Link 
              to="/create"
              className="block w-full bg-[#D92B2B] hover:bg-[#C02424] text-white py-4 rounded-xl font-semibold text-lg transition-all"
            >
              Create Your Card Now
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            {/* Brand */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-[#D92B2B] rounded-lg flex items-center justify-center">
                  <QrCode className="w-4 h-4 text-white" />
                </div>
                <span className="font-semibold text-white">My EmergencyCard</span>
              </div>
              <p className="text-sm text-gray-500 mb-4 max-w-xs">
                Digital emergency medical cards that save lives. A product by Hikari Grove.
              </p>
              <p className="text-xs text-gray-600">
                © 2026 Hikari Grove. All rights reserved.
              </p>
            </div>
            
            {/* Links */}
            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/create" className="hover:text-white transition-colors">Create Card</Link></li>
                <li><Link to="/how-it-works" className="hover:text-white transition-colors">How It Works</Link></li>
                <li><a href="https://emergencycard.health" className="hover:text-white transition-colors">Health Passport</a></li>
              </ul>
            </div>
            
            {/* Legal */}
            <div>
              <h4 className="font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link></li>
                <li><Link to="/contact" className="hover:text-white transition-colors">Contact Us</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-4 text-xs text-gray-600">
              <span>🔒 Your data stays with you</span>
              <span>•</span>
              <span>No account required</span>
              <span>•</span>
              <span>Works offline</span>
            </div>
            <a 
              href="https://hikarigrove.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-xs text-gray-500 hover:text-white transition-colors"
            >
              A Hikari Grove Product
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ProductLandingPage;
